import { createSlice } from '@reduxjs/toolkit';
import { FlowOfFundsState } from './flow-of-funds.state';
import { GetFlowOfFunds } from './thunks/get-flow-of-funds.thunk';
import { GetDeferredPaymentSchedule } from './thunks/get-deferred-payment-schedule.thunk';
import { GetBuyerPaymentObligations } from './thunks/get-buyer-payment-obligations.thunk';
import { ConfirmWireSent } from './thunks/confirm-wire-sent.thunk';
import { ConfirmWireReceipt } from './thunks/confirm-wire-receipt.thunk';
import { AgreeToVetValueFees } from './thunks/agree-to-vetvalue-fees.thunk';
import { UpdateVetValueFeeWireInstructions } from './thunks/update-vetvalue-fee-wire-instructions.thunk';
import { UploadSenderEvidence } from './thunks/upload-sender-evidence.thunk';
import { RemoveSenderEvidence } from './thunks/remove-sender-evidence.thunk';
import { UploadReceiptEvidence } from './thunks/upload-receipt-evidence.thunk';
import { RemoveReceiptEvidence } from './thunks/remove-receipt-evidence.thunk';
import { UploadPayoffConfirmationDocument } from './thunks/upload-payoff-confirmation-document.thunk';
import { RemovePayoffConfirmationDocument } from './thunks/remove-payoff-confirmation-document.thunk';
import { AddThirdPartyPayoffRow } from './thunks/add-third-party-payoff-row.thunk';
import { DeleteThirdPartyPayoffRow } from './thunks/delete-third-party-payoff-row.thunk';
import { SaveThirdPartyPayoffRow } from './thunks/save-third-party-payoff-row.thunk';
import { UpdateThirdPartyPayoffWireInstructions } from './thunks/update-third-party-payoff-wire-instructions.thunk';
import { UploadThirdPartyPayoffSupportingDocument } from './thunks/upload-third-party-payoff-supporting-document.thunk';
import { DeleteThirdPartyPayoffSupportingDocument } from './thunks/delete-third-party-payoff-supporting-document.thunk';
import { SetThirdPartyPayoffApproval } from './thunks/set-third-party-payoff-approval.thunk';
import { SetThirdPartyPayoffBuyerAssumption } from './thunks/set-third-party-payoff-buyer-assumption.thunk';
import { SetThirdPartyPayoffPayoffAmount } from './thunks/set-third-party-payoff-payoff-amount.thunk';
import { ProposeThirdPartyPayoffRemoval } from './thunks/propose-third-party-payoff-removal.thunk';
import { RespondToThirdPartyPayoffRemoval } from './thunks/respond-to-third-party-payoff-removal.thunk';
import { SetLegalFeesAmount } from './thunks/set-legal-fees-amount.thunk';
import { UploadLegalFeesInvoice } from './thunks/upload-legal-fees-invoice.thunk';
import { DeleteLegalFeesInvoice } from './thunks/delete-legal-fees-invoice.thunk';
import { UpdateLegalFeesWireInstructions } from './thunks/update-legal-fees-wire-instructions.thunk';
import { SetLegalFeesWireInstructionsVerification } from './thunks/set-legal-fees-wire-instructions-verification.thunk';
import { SetLegalFeesAgreement } from './thunks/set-legal-fees-agreement.thunk';
import {
    mergeThirdPartyPayoffRowSaveResponse,
    mergeThirdPartyPayoffWriteResponse,
} from './third-party-payoff-merge';
import { LegalFees, ThirdPartyPayoff } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { StoreState } from '../../../store/store-state';

// Row-level write responses only carry the FoF-owned fields — the merge keeps
// the composition-owned cells/isAssumedByBuyer/payoffAmount already in state
// (see third-party-payoff-merge.ts). A response for an unknown row is appended
// (the add-row path).
const applyThirdPartyPayoffWriteResponse = (
    state: FlowOfFundsState,
    response: ThirdPartyPayoff
) => {
    const rows = state.flowOfFunds?.thirdPartyPayoffs?.rows;
    if (!rows) {
        return;
    }
    const index = rows.findIndex((row) => row.tableRowId === response.tableRowId);
    if (index >= 0) {
        rows[index] = mergeThirdPartyPayoffWriteResponse(rows[index], response);
    } else {
        rows.push(response);
    }
};

const applyLegalFeesResponse = (state: FlowOfFundsState, response: LegalFees) => {
    if (!state.flowOfFunds) {
        return;
    }
    state.flowOfFunds.legalFees = response;
};

const isResponseForCurrentSelection = (
    state: FlowOfFundsState,
    respondedEngagementId: number
): boolean =>
    state.flowOfFundsRequestedEngagementId === undefined ||
    state.flowOfFundsRequestedEngagementId === respondedEngagementId;

const removeThirdPartyPayoffRow = (state: FlowOfFundsState, tableRowId: number) => {
    const section = state.flowOfFunds?.thirdPartyPayoffs;
    if (!section) {
        return;
    }
    section.rows = section.rows.filter((row) => row.tableRowId !== tableRowId);
};

const initialState: FlowOfFundsState = {
    flowOfFunds: undefined,
    flowOfFundsEngagementId: undefined,
    flowOfFundsRequestedEngagementId: undefined,
    flowOfFundsLoading: false,
    flowOfFundsError: '',
    deferredPayments: undefined,
    deferredPaymentsLoading: false,
    deferredPaymentsError: '',
    buyerObligations: undefined,
    buyerObligationsLoading: false,
    buyerObligationsError: '',
};

const flowOfFundsSlice = createSlice({
    name: 'flowOfFunds',
    initialState,
    reducers: {
        resetFlowOfFunds() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(GetFlowOfFunds.pending, (state, action) => {
                state.flowOfFundsRequestedEngagementId = action.meta.arg;
                // A refetch of the engagement already on screen (add row, DAQI
                // file change, conflict recovery) must not blank the rendered
                // content — the loading flag only gates the FIRST load of an
                // engagement; a background refresh swaps data in on fulfilled.
                const isRefreshOfLoadedEngagement =
                    state.flowOfFunds !== undefined &&
                    state.flowOfFundsEngagementId === action.meta.arg;
                state.flowOfFundsLoading = !isRefreshOfLoadedEngagement;
                state.flowOfFundsError = '';
            })
            .addCase(GetFlowOfFunds.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg)) {
                    return;
                }
                state.flowOfFunds = action.payload;
                state.flowOfFundsEngagementId = action.meta.arg;
                state.flowOfFundsLoading = false;
                state.flowOfFundsError = '';
            })
            .addCase(GetFlowOfFunds.rejected, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg)) {
                    return;
                }
                state.flowOfFundsLoading = false;
                state.flowOfFundsError =
                    action.error.message || 'Failed to fetch the flow of funds';
            })

            .addCase(ConfirmWireSent.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(ConfirmWireReceipt.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(AgreeToVetValueFees.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(UpdateVetValueFeeWireInstructions.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(UploadSenderEvidence.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(RemoveSenderEvidence.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(UploadReceiptEvidence.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(RemoveReceiptEvidence.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(UploadPayoffConfirmationDocument.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })
            .addCase(RemovePayoffConfirmationDocument.fulfilled, (state, action) => {
                state.flowOfFunds = action.payload;
            })

            .addCase(GetDeferredPaymentSchedule.pending, (state) => {
                state.deferredPaymentsLoading = true;
                state.deferredPaymentsError = '';
            })
            .addCase(GetDeferredPaymentSchedule.fulfilled, (state, action) => {
                state.deferredPayments = action.payload;
                state.deferredPaymentsLoading = false;
                state.deferredPaymentsError = '';
            })
            .addCase(GetDeferredPaymentSchedule.rejected, (state, action) => {
                state.deferredPaymentsLoading = false;
                state.deferredPaymentsError =
                    action.error.message ||
                    'Failed to fetch the deferred payment schedule';
            })

            .addCase(GetBuyerPaymentObligations.pending, (state) => {
                state.buyerObligationsLoading = true;
                state.buyerObligationsError = '';
            })
            .addCase(GetBuyerPaymentObligations.fulfilled, (state, action) => {
                state.buyerObligations = action.payload;
                state.buyerObligationsLoading = false;
                state.buyerObligationsError = '';
            })
            .addCase(GetBuyerPaymentObligations.rejected, (state, action) => {
                state.buyerObligationsLoading = false;
                state.buyerObligationsError =
                    action.error.message || 'Failed to fetch buyer payment obligations';
            })

            .addCase(AddThirdPartyPayoffRow.fulfilled, (state, action) => {
                applyThirdPartyPayoffWriteResponse(state, action.payload);
            })
            .addCase(DeleteThirdPartyPayoffRow.fulfilled, (state, action) => {
                removeThirdPartyPayoffRow(state, action.meta.arg.tableRowId);
            })
            .addCase(SaveThirdPartyPayoffRow.fulfilled, (state, action) => {
                const rows = state.flowOfFunds?.thirdPartyPayoffs?.rows;
                if (!rows) {
                    return;
                }
                const index = rows.findIndex(
                    (row) => row.tableRowId === action.payload.row.tableRowId
                );
                const merged = mergeThirdPartyPayoffRowSaveResponse(
                    index >= 0 ? rows[index] : undefined,
                    action.payload
                );
                if (index >= 0) {
                    rows[index] = merged;
                } else {
                    rows.push(merged);
                }
            })
            .addCase(
                UpdateThirdPartyPayoffWireInstructions.fulfilled,
                (state, action) => {
                    applyThirdPartyPayoffWriteResponse(state, action.payload);
                }
            )
            .addCase(
                UploadThirdPartyPayoffSupportingDocument.fulfilled,
                (state, action) => {
                    applyThirdPartyPayoffWriteResponse(state, action.payload);
                }
            )
            .addCase(
                DeleteThirdPartyPayoffSupportingDocument.fulfilled,
                (state, action) => {
                    applyThirdPartyPayoffWriteResponse(state, action.payload);
                }
            )
            .addCase(SetThirdPartyPayoffApproval.fulfilled, (state, action) => {
                applyThirdPartyPayoffWriteResponse(state, action.payload);
            })
            .addCase(SetThirdPartyPayoffBuyerAssumption.fulfilled, (state, action) => {
                applyThirdPartyPayoffWriteResponse(state, action.payload);
                const rows = state.flowOfFunds?.thirdPartyPayoffs?.rows;
                const row = rows?.find(
                    (candidate) => candidate.tableRowId === action.meta.arg.tableRowId
                );
                if (row) {
                    row.isAssumedByBuyer = action.meta.arg.isAssumedByBuyer;
                }
            })
            .addCase(SetThirdPartyPayoffPayoffAmount.fulfilled, (state, action) => {
                applyThirdPartyPayoffWriteResponse(state, action.payload);
                const rows = state.flowOfFunds?.thirdPartyPayoffs?.rows;
                const row = rows?.find(
                    (candidate) => candidate.tableRowId === action.meta.arg.tableRowId
                );
                if (row) {
                    row.payoffAmount = action.meta.arg.payoffAmount;
                }
            })
            .addCase(ProposeThirdPartyPayoffRemoval.fulfilled, (state, action) => {
                applyThirdPartyPayoffWriteResponse(state, action.payload);
            })
            .addCase(
                RespondToThirdPartyPayoffRemoval.fulfilled,
                (state, action) => {
                    if (action.meta.arg.approve) {
                        removeThirdPartyPayoffRow(state, action.meta.arg.tableRowId);
                    } else {
                        applyThirdPartyPayoffWriteResponse(state, action.payload);
                    }
                }
            )

            .addCase(SetLegalFeesAmount.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                applyLegalFeesResponse(state, action.payload);
            })
            .addCase(UploadLegalFeesInvoice.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                applyLegalFeesResponse(state, action.payload);
            })
            .addCase(DeleteLegalFeesInvoice.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                applyLegalFeesResponse(state, action.payload);
            })
            .addCase(UpdateLegalFeesWireInstructions.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                applyLegalFeesResponse(state, action.payload);
            })
            .addCase(
                SetLegalFeesWireInstructionsVerification.fulfilled,
                (state, action) => {
                    if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                        return;
                    }
                    applyLegalFeesResponse(state, action.payload);
                }
            )
            .addCase(SetLegalFeesAgreement.fulfilled, (state, action) => {
                if (!isResponseForCurrentSelection(state, action.meta.arg.engagementId)) {
                    return;
                }
                applyLegalFeesResponse(state, action.payload);
            });
    },
});

export const selectFlowOfFunds = (state: StoreState, engagementId: number) =>
    state.flowOfFunds.flowOfFundsEngagementId === engagementId
        ? state.flowOfFunds.flowOfFunds
        : undefined;
export const selectFlowOfFundsLoading = (state: StoreState) =>
    state.flowOfFunds.flowOfFundsLoading;
export const selectFlowOfFundsRequested = (state: StoreState, engagementId: number) =>
    state.flowOfFunds.flowOfFundsRequestedEngagementId === engagementId;
export const selectDeferredPaymentSchedule = (state: StoreState) =>
    state.flowOfFunds.deferredPayments;
export const selectDeferredPaymentScheduleLoading = (state: StoreState) =>
    state.flowOfFunds.deferredPaymentsLoading;
export const selectBuyerPaymentObligations = (state: StoreState) =>
    state.flowOfFunds.buyerObligations;
export const selectBuyerPaymentObligationsLoading = (state: StoreState) =>
    state.flowOfFunds.buyerObligationsLoading;

export const { resetFlowOfFunds } = flowOfFundsSlice.actions;
export default flowOfFundsSlice.reducer;
